import React, { useState, useEffect, useCallback } from 'react';
import { supabase, Site } from '../lib/supabase';
import { fetchWeatherForSite, getWeatherInfo, isRainAlert, WeatherData } from '../utils/weatherService';
import {
  Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, CloudDrizzle,
  CloudSun, Sun, Wind, Droplets, AlertTriangle, RefreshCw, MapPin, Building2,
} from 'lucide-react';

type WeatherIcon = { className?: string };

const iconMap: Record<string, React.FC<WeatherIcon>> = {
  'sun': Sun,
  'cloud-sun': CloudSun,
  'cloud': Cloud,
  'cloud-fog': CloudFog,
  'cloud-drizzle': CloudDrizzle,
  'cloud-rain': CloudRain,
  'cloud-snow': CloudSnow,
  'cloud-lightning': CloudLightning,
};

const WeatherIcon: React.FC<{ name: string; className?: string }> = ({ name, className }) => {
  const Icon = iconMap[name] || Cloud;
  return <Icon className={className} />;
};

const formatDay = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { weekday: 'short' });
};

const formatDateShort = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

interface SiteWeatherState {
  site: Site;
  weather: WeatherData | null;
  loading: boolean;
  error: string | null;
}

export const WeatherPage: React.FC = () => {
  const [sitesWeather, setSitesWeather] = useState<SiteWeatherState[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadWeather = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data, error } = await supabase
        .from('sites')
        .select('*')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;

      const activeSites: Site[] = data || [];
      const initial: SiteWeatherState[] = activeSites.map(s => ({
        site: s,
        weather: null,
        loading: true,
        error: null,
      }));
      setSitesWeather(initial);
      setLoading(false);

      activeSites.forEach(async (site, index) => {
        try {
          const weather = await fetchWeatherForSite(site);
          setSitesWeather(prev => {
            const next = [...prev];
            next[index] = { site, weather, loading: false, error: null };
            return next;
          });
        } catch (err) {
          setSitesWeather(prev => {
            const next = [...prev];
            next[index] = { site, weather: null, loading: false, error: err instanceof Error ? err.message : 'Failed to load weather' };
            return next;
          });
        }
      });
    } catch (err) {
      console.error('Error fetching sites:', err);
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadWeather();
  }, [loadWeather]);

  const alertCount = sitesWeather.filter(sw =>
    sw.weather?.forecast.some(d => isRainAlert(d.precipitation))
  ).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Site Weather</h1>
          <p className="text-sm text-gray-500 mt-1">7-day forecast and rainfall history for all active sites</p>
        </div>
        <button
          onClick={loadWeather}
          disabled={refreshing}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Rain Alert Summary Banner */}
      {alertCount > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-center gap-3">
          <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-900">
              {alertCount} {alertCount === 1 ? 'site has' : 'sites have'} rain over 10mm expected in the next 7 days
            </p>
            <p className="text-xs text-amber-700 mt-0.5">Review the highlighted day tiles below for details.</p>
          </div>
        </div>
      )}

      {sitesWeather.length === 0 && !loading && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No active sites</h3>
          <p className="text-gray-500">Add active sites to see weather forecasts here.</p>
        </div>
      )}

      {/* Site Weather Cards */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {sitesWeather.map((sw) => (
          <SiteWeatherCard key={sw.site.id} state={sw} />
        ))}
      </div>
    </div>
  );
};

const SiteWeatherCard: React.FC<{ state: SiteWeatherState }> = ({ state }) => {
  const { site, weather, loading, error } = state;
  const hasAlert = weather?.forecast.some(d => isRainAlert(d.precipitation));

  return (
    <div className={`bg-white rounded-lg shadow-sm border p-5 ${hasAlert ? 'border-amber-300' : 'border-gray-200'}`}>
      {/* Card Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Building2 className="h-5 w-5 text-orange-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-gray-900 truncate">{site.name}</h3>
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{site.address}</span>
          </div>
        </div>
        {hasAlert && (
          <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex-shrink-0">
            <AlertTriangle className="h-3 w-3" />
            Rain Alert
          </div>
        )}
      </div>

      {/* Body */}
      {loading && (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-2 py-6 text-sm text-red-600 bg-red-50 rounded-md px-4">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {weather && !loading && (
        <>
          {/* Past 7 Days Rainfall */}
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-blue-50 rounded-md">
            <Droplets className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span className="text-sm text-blue-900">
              Past 7 days rainfall: <span className="font-semibold">{weather.past7DaysRainfall.toFixed(1)} mm</span>
            </span>
          </div>

          {/* 7-Day Forecast */}
          <div className="grid grid-cols-7 gap-1.5">
            {weather.forecast.map((day, i) => {
              const info = getWeatherInfo(day.weatherCode);
              const alert = isRainAlert(day.precipitation);
              return (
                <div
                  key={i}
                  className={`flex flex-col items-center text-center px-1 py-2.5 rounded-lg transition-colors ${
                    alert
                      ? 'bg-amber-50 border border-amber-300'
                      : 'bg-gray-50 border border-gray-100'
                  }`}
                >
                  <span className="text-xs font-medium text-gray-600">{formatDay(day.date)}</span>
                  <span className="text-[10px] text-gray-400 mb-1">{formatDateShort(day.date)}</span>
                  <WeatherIcon name={info.icon} className={`h-7 w-7 mb-1 ${alert ? 'text-amber-500' : 'text-gray-400'}`} />
                  <span className="text-xs font-semibold text-gray-900">{Math.round(day.tempMax)}°</span>
                  <span className="text-[10px] text-gray-500">{Math.round(day.tempMin)}°</span>
                  <div className="flex items-center gap-0.5 mt-1">
                    <Droplets className={`h-3 w-3 ${alert ? 'text-amber-500' : 'text-blue-400'}`} />
                    <span className={`text-[10px] ${alert ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>
                      {day.precipitation.toFixed(1)}mm
                    </span>
                  </div>
                  <div className="flex items-center gap-0.5 mt-0.5">
                    <Wind className="h-3 w-3 text-gray-400" />
                    <span className="text-[10px] text-gray-500">{Math.round(day.windSpeedMax)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 text-[11px] text-gray-400">
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-amber-50 border border-amber-300" />
              Rain &gt; 10mm
            </span>
            <span className="flex items-center gap-1">
              <Wind className="h-3 w-3" /> km/h max
            </span>
          </div>
        </>
      )}
    </div>
  );
};
